import { useEffect, useState } from "react";
import client, { formatApiError, inr, API } from "@/lib/api";
import { toast } from "sonner";
import { Truck, FileText, MapPin, Loader2, X } from "lucide-react";

const badgeMap = {
  unshipped: "bg-[#EBE3D5] text-[#7B6F66]",
  booked: "bg-[#F4EFE6] text-[#1C3326] border border-[#1C3326]",
  in_transit: "bg-[#1C3326] text-[#FDFBF7]",
  delivered: "bg-[#C85A32] text-[#FDFBF7]",
  cancelled: "bg-red-100 text-red-800",
};

export function ShippingBadge({ status }) {
  const label = (status || "unshipped").replace(/_/g, " ");
  return (
    <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] ${badgeMap[status] || badgeMap.unshipped}`}>
      {label}
    </span>
  );
}

export default function OrderDetailModal({ orderId, onClose, onChange }) {
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tracking, setTracking] = useState(null);
  const [showTrack, setShowTrack] = useState(false);

  const load = async () => {
    try {
      const { data } = await client.get("/orders");
      const o = data.find((x) => x.id === orderId);
      setOrder(o || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const ship = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`/orders/${orderId}/ship`);
      setOrder(data.order);
      toast.success("Pushed to Shiprocket");
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const track = async () => {
    setBusy(true);
    setShowTrack(true);
    try {
      const { data } = await client.get(`/orders/${orderId}/track`);
      setTracking(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const genLabel = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`/orders/${orderId}/label`);
      const url = data.label_url || data.label;
      if (url) window.open(url, "_blank");
      else toast.info("Label queued. Try again in a minute.");
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!order) {
    return (
      <div className="fixed inset-0 z-[100] bg-[#2C1E16]/70 backdrop-blur-sm flex items-center justify-center">
        <Loader2 className="animate-spin text-[#FDFBF7]" />
      </div>
    );
  }

  const invoiceUrl = `${API}/orders/${order.id}/invoice.pdf`;

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#2C1E16]/70 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto"
      onClick={onClose}
      data-testid="order-detail-modal"
    >
      <div className="bg-[#FDFBF7] max-w-3xl w-full my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start px-8 pt-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-2">
              Order · {order.status?.toUpperCase()}
            </div>
            <div className="font-serif-display text-3xl text-[#1C3326]">{order.order_number}</div>
            <div className="text-xs text-[#7B6F66] mt-1">
              {new Date(order.created_at).toLocaleString()} · {order.email}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:text-[#C85A32]" data-testid="order-detail-close"><X size={20} /></button>
        </div>

        <div className="px-8 py-6 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div className="border border-[#EBE3D5] p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#7B6F66] mb-2">Ship to</div>
            <b>{order.address.full_name}</b><br />
            {order.address.line1}{order.address.line2 && `, ${order.address.line2}`}<br />
            {order.address.city}, {order.address.state} {order.address.pincode}<br />
            <span className="text-[#7B6F66]">📞 {order.address.phone}</span>
          </div>
          <div className="border border-[#EBE3D5] p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#7B6F66] mb-2">Payment</div>
            Method: <b>{order.payment_method?.toUpperCase()}</b><br />
            Status: <b>{order.payment_status?.toUpperCase()}</b>
            {order.razorpay_payment_id && <div className="text-xs text-[#7B6F66] mt-1">Payment ID: {order.razorpay_payment_id}</div>}
            <div className="mt-3 pt-3 border-t border-[#EBE3D5] text-lg font-serif-display">
              Total: {inr(order.total)}
            </div>
          </div>
        </div>

        <div className="px-8 pb-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#7B6F66] mb-2">Items</div>
          <div className="border border-[#EBE3D5]">
            {order.items.map((it, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3 border-b border-[#EBE3D5] last:border-b-0">
                <img src={it.image} alt="" className="w-10 h-12 object-cover bg-[#F4EFE6]" />
                <div className="flex-1 text-sm">
                  <div className="font-medium">{it.name}</div>
                  <div className="text-xs text-[#7B6F66]">{it.variant} · qty {it.quantity}</div>
                </div>
                <div className="text-sm">{inr(it.price * it.quantity)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Shipping panel */}
        <div className="px-8 pb-6">
          <div className="border border-[#EBE3D5] p-5">
            <div className="flex justify-between items-center mb-3">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#7B6F66]">Shipment</div>
              <ShippingBadge status={order.shipping_status} />
            </div>
            {order.awb_code ? (
              <div className="text-sm space-y-1">
                <div><b>AWB:</b> {order.awb_code}</div>
                <div><b>Courier:</b> {order.courier_name || "—"}</div>
                {order.tracking_url && (
                  <a href={order.tracking_url} target="_blank" rel="noreferrer" className="text-[#C85A32] link-underline">Tracking link</a>
                )}
              </div>
            ) : order.shiprocket_order_id ? (
              <div className="text-sm text-[#7B6F66]">Booked with Shiprocket (ID {order.shiprocket_order_id}). AWB will be assigned shortly.</div>
            ) : (
              <div className="text-sm text-[#7B6F66]">Not shipped yet.</div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="px-8 pb-8 flex flex-wrap gap-3">
          <a
            href={invoiceUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 border border-[#1C3326] hover:bg-[#1C3326] hover:text-[#FDFBF7] px-6 py-3 uppercase text-xs tracking-[0.25em] transition-colors"
            data-testid="download-invoice"
          >
            <FileText size={14} /> Download Invoice
          </a>
          {!order.shiprocket_order_id && (
            <button
              onClick={ship}
              disabled={busy}
              data-testid="push-shiprocket"
              className="inline-flex items-center gap-2 bg-[#C85A32] hover:bg-[#A84A28] text-[#FDFBF7] px-6 py-3 uppercase text-xs tracking-[0.25em] disabled:opacity-60"
            >
              <Truck size={14} /> {busy ? "Pushing…" : "Push to Shiprocket"}
            </button>
          )}
          {order.shiprocket_order_id && (
            <>
              <button
                onClick={track}
                disabled={busy}
                data-testid="track-shipment"
                className="inline-flex items-center gap-2 border border-[#1C3326] hover:bg-[#1C3326] hover:text-[#FDFBF7] px-6 py-3 uppercase text-xs tracking-[0.25em]"
              >
                <MapPin size={14} /> Track
              </button>
              <button
                onClick={genLabel}
                disabled={busy}
                data-testid="generate-label"
                className="inline-flex items-center gap-2 border border-[#1C3326] hover:bg-[#1C3326] hover:text-[#FDFBF7] px-6 py-3 uppercase text-xs tracking-[0.25em]"
              >
                <FileText size={14} /> Generate Label
              </button>
            </>
          )}
        </div>

        {showTrack && (
          <div className="mx-8 mb-8 border border-[#EBE3D5] p-5">
            <div className="flex justify-between items-center mb-3">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#7B6F66]">Live tracking</div>
              <button onClick={() => setShowTrack(false)}><X size={14} /></button>
            </div>
            {busy && <Loader2 className="animate-spin" />}
            {tracking && (
              <pre className="text-xs whitespace-pre-wrap max-h-64 overflow-auto bg-[#F4EFE6] p-3">
                {JSON.stringify(tracking, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
